import { useTranslation } from 'react-i18next';
import SVGIcon from '../atoms/icons';
import Text from '../atoms/text';
import Select from '../atoms/select';
import classNames from 'classnames';
import { EditableChangeEvent } from '../../utils/types/editable';

const pageOptions = ['10', '25', '50'];

export interface PaginationProps {
	pageSize: number;
	pageCurrent: number;
	totalPages: number;
	totalItems: number;
	onUpdate?: (pageSize: number, pageCurrent: number) => void;
}

const Pagination = ({
	pageSize,
	pageCurrent,
	totalPages,
	totalItems,
	onUpdate,
}: PaginationProps) => {
	const { t } = useTranslation();

	// Função para calcular a faixa de itens exibidos
	const calcRange = () => {
		const start = Math.min((pageCurrent - 1) * pageSize + 1, totalItems);
		const end = Math.min(start + pageSize - 1, totalItems);
		return `${start}-${end} ${t('pagination.of')} ${totalItems}`;
	};

	// Atualiza a página para a esquerda
	const onLeftClick = () => {
		if (pageCurrent > 1) {
			onUpdate?.(pageSize, pageCurrent - 1);
		}
	};

	// Atualiza a página para a direita
	const onRightClick = () => {
		if (pageCurrent < totalPages) {
			onUpdate?.(pageSize, pageCurrent + 1);
		}
	};

	// Atualiza o tamanho da página
	const handleSelect = (e: EditableChangeEvent) =>
		onUpdate?.(Number(e.target.value), 1);

	return (
		<div className="flex flex-col sm:flex-row gap-3 justify-between sm:justify-end items-center sm:items-center w-full">
			{/* Label de tamanho da página */}
			<div className="flex items-center gap-2">
				<label htmlFor="page_size" className="text-sm">
					{t('pagination.lines_by_page')}:
				</label>

				{/* Componente de Select para escolher o tamanho da página */}
				<Select
					className="w-14"
					boxClassName="border-0"
					placeholderClassName="text-sm"
					name="page_size"
					value={pageSize}
					options={pageOptions}
					onChange={handleSelect}
				/>
			</div>

			{/* Exibe a faixa de itens exibidos */}
			<Text className="hidden sm:block text-sm">{calcRange()}</Text>

			{/* Navegação à esquerda */}
			<div className="flex items-center gap-3 sm:gap-5">
				<SVGIcon
					className={classNames('h-2 w-auto select-none', {
						'fill-gray-txt cursor-pointer': pageCurrent > 1,
						'fill-gray pointer-events-none': pageCurrent <= 1,
					})}
					icon="Arrow"
					direction="left"
					onClick={onLeftClick}
				/>

				{/* Navegação à direita */}
				<SVGIcon
					className={classNames('h-2 w-auto select-none', {
						'fill-gray-txt cursor-pointer':
							pageCurrent < totalPages,
						'fill-gray pointer-events-none':
							pageCurrent >= totalPages,
					})}
					icon="Arrow"
					direction="right"
					onClick={onRightClick}
				/>
			</div>

			{/* Faixa de itens exibidos na versão mobile */}
			<Text className="block sm:hidden text-sm">{calcRange()}</Text>
		</div>
	);
};

export default Pagination;
